import { useState } from "react";

interface BeforeAfterSliderPreviewProps {
  settings: {
    beforeImage?: string;
    afterImage?: string;
    beforeLabel?: string;
    afterLabel?: string;
    height?: string;
    borderRadius?: string;
    showLabels?: boolean;
  };
}

export const BeforeAfterSliderPreview = ({ settings }: BeforeAfterSliderPreviewProps) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);

  const handleMove = (clientX: number, rect: DOMRect) => {
    const x = clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    setSliderPosition(Math.max(0, Math.min(100, percentage)));
  };

  const handleMouseDown = () => setIsDragging(true);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      const rect = e.currentTarget.getBoundingClientRect();
      handleMove(e.clientX, rect);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches[0]) {
      const rect = e.currentTarget.getBoundingClientRect();
      handleMove(e.touches[0].clientX, rect);
    }
  };

  return (
    <div className="py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div
          className="relative select-none overflow-hidden cursor-col-resize"
          style={{
            height: `${settings.height || 400}px`,
            borderRadius: `${settings.borderRadius || 12}px`,
          }}
          onMouseMove={handleMouseMove}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchMove={handleTouchMove}
          onTouchStart={handleMouseDown}
          onTouchEnd={handleMouseUp}
        >
          {/* After Image (Background) */}
          <div className="absolute inset-0">
            <img
              src={settings.afterImage || "/placeholder.svg"}
              alt={settings.afterLabel || "بعد"}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {settings.showLabels && (
              <div className="absolute top-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                {settings.afterLabel || "بعد"}
              </div>
            )}
          </div>

          {/* Before Image (Clipped) */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
          >
            <img
              src={settings.beforeImage || "/placeholder.svg"}
              alt={settings.beforeLabel || "قبل"}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
            />
            {settings.showLabels && (
              <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-lg font-bold shadow-lg">
                {settings.beforeLabel || "قبل"}
              </div>
            )}
          </div>

          {/* Slider Line */}
          <div
            className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-col-resize"
            style={{ left: `${sliderPosition}%` }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
              <div className="flex gap-1">
                <div className="w-1 h-4 bg-gray-400 rounded"></div>
                <div className="w-1 h-4 bg-gray-400 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
